import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Car, Shield, Eye, EyeOff, CheckCircle2, Mail, Phone, MessageSquare, User } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ nome: "", email: "", password: "", confirmPassword: "" });
  const [contactData, setContactData] = useState({ nome: "", email: "", contacto: "", mensagem: "" });
  const [contactSubmitting, setContactSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF]"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const result = await login(loginData.email, loginData.password);
    if (result.success) {
      navigate("/dashboard");
    }
    setIsSubmitting(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast.error("As passwords não coincidem");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        nome: registerData.nome,
        email: registerData.email,
        password: registerData.password
      });
      
      if (response.data.requires_approval) {
        setRegistrationSuccess(true);
      } else {
        // First user (admin) - can login directly
        toast.success("Conta de administrador criada. Por favor faça login.");
        setActiveTab("login");
        setLoginData({ email: registerData.email, password: "" });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao efetuar registo");
    }
    setIsSubmitting(false);
  };

  const handleContact = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactData);
      toast.success("Pedido de contacto enviado com sucesso!");
      setContactData({ nome: "", email: "", contacto: "", mensagem: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar pedido de contacto");
    }
    setContactSubmitting(false);
  };

  // Show success message after registration
  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row">
        {/* Left side - Branding */}
        <div className="lg:w-1/2 bg-[#0047FF] p-8 lg:p-16 flex flex-col justify-between min-h-[30vh] lg:min-h-screen">
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                <Car className="w-7 h-7 text-[#0047FF]" />
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold text-white tracking-tight">GAPE</h1>
            </div>
            <p className="text-white/90 text-lg lg:text-xl max-w-md leading-relaxed">
              Gestão de Acesso de Parques de Estacionamento
            </p>
          </div>
        </div>

        {/* Right side - Success Message */}
        <div className="lg:w-1/2 flex items-center justify-center p-6 lg:p-16 bg-[#FAFAFA]">
          <Card className="w-full max-w-md border-[#E2E8F0] shadow-sm">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-[#00C853]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-[#00C853]" />
              </div>
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Registo Efectuado</h2>
              <p className="text-[#64748B] mb-6 leading-relaxed">
                Aguarde a validação do mesmo.
              </p>
              <p className="text-[#0047FF] font-medium">A equipa GAPE</p>
              <Button 
                onClick={() => {
                  setRegistrationSuccess(false);
                  setActiveTab("login");
                }}
                className="mt-6 bg-[#0047FF] hover:bg-[#0035CC]"
                data-testid="back-to-login-btn"
              >
                Voltar ao Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left side - Branding */}
      <div className="lg:w-1/2 bg-[#0047FF] p-8 lg:p-16 flex flex-col justify-between min-h-[30vh] lg:min-h-screen">
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
              <Car className="w-7 h-7 text-[#0047FF]" />
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white tracking-tight">GAPE</h1>
          </div>
          <p className="text-white/90 text-lg lg:text-xl max-w-md leading-relaxed">
            Gestão de Acesso de Parques de Estacionamento
          </p>
        </div>
        
        <div className="hidden lg:block">
          <div className="flex items-center gap-4 mb-6">
            <Shield className="w-8 h-8 text-white/80" />
            <div>
              <p className="text-white font-medium">Controlo Seguro</p>
              <p className="text-white/70 text-sm">Gestão de quotas e acessos via QR Code</p>
            </div>
          </div>
          <p className="text-white/60 text-sm">
            Plataforma SaaS para gestão de parques de estacionamento
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="lg:w-1/2 flex items-center justify-center p-6 lg:p-16 bg-[#FAFAFA]">
        <Card className="w-full max-w-md border-[#E2E8F0] shadow-sm">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-bold text-[#0F172A]">Bem-vindo</CardTitle>
            <CardDescription className="text-[#64748B]">
              Aceda à sua conta, registe-se ou contacte-nos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Entrar</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Registar</TabsTrigger>
                <TabsTrigger value="contact" data-testid="contact-tab">Contacto</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-[#0F172A] font-medium">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      data-testid="login-email-input"
                      className="h-11 border-[#E2E8F0] focus:border-[#0047FF] focus:ring-[#0047FF]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-[#0F172A] font-medium">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        data-testid="login-password-input"
                        className="h-11 pr-10 border-[#E2E8F0] focus:border-[#0047FF] focus:ring-[#0047FF]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0F172A]"
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-[#0047FF] hover:bg-[#0035CC] text-white font-medium"
                    disabled={isSubmitting}
                    data-testid="login-submit-btn"
                  >
                    {isSubmitting ? "A entrar..." : "Entrar"}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="register-nome" className="text-[#0F172A] font-medium">Nome</Label>
                    <Input
                      id="register-nome"
                      type="text"
                      placeholder="O seu nome"
                      value={registerData.nome}
                      onChange={(e) => setRegisterData({ ...registerData, nome: e.target.value })}
                      required
                      data-testid="register-nome-input"
                      className="h-11 border-[#E2E8F0] focus:border-[#0047FF] focus:ring-[#0047FF]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-[#0F172A] font-medium">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                      className="h-11 border-[#E2E8F0] focus:border-[#0047FF] focus:ring-[#0047FF]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-[#0F172A] font-medium">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      data-testid="register-password-input"
                      className="h-11 border-[#E2E8F0] focus:border-[#0047FF] focus:ring-[#0047FF]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm-password" className="text-[#0F172A] font-medium">Confirmar Password</Label>
                    <Input
                      id="register-confirm-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                      required
                      data-testid="register-confirm-password-input"
                      className="h-11 border-[#E2E8F0] focus:border-[#0047FF] focus:ring-[#0047FF]"
                    />
                    {registerData.password !== registerData.confirmPassword && registerData.confirmPassword && (
                      <p className="text-sm text-[#D50000]">As passwords não coincidem</p>
                    )}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-[#0047FF] hover:bg-[#0035CC] text-white font-medium"
                    disabled={isSubmitting || registerData.password !== registerData.confirmPassword}
                    data-testid="register-submit-btn"
                  >
                    {isSubmitting ? "A registar..." : "Criar Conta"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="contact">
                <form onSubmit={handleContact} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="contact-nome" className="text-[#0F172A] font-medium">Nome</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input
                        id="contact-nome"
                        type="text"
                        placeholder="O seu nome"
                        value={contactData.nome}
                        onChange={(e) => setContactData({ ...contactData, nome: e.target.value })}
                        required
                        data-testid="contact-nome-input"
                        className="h-11 pl-10 border-[#E2E8F0] focus:border-[#0047FF] focus:ring-[#0047FF]"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email" className="text-[#0F172A] font-medium">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input
                        id="contact-email"
                        type="email"
                        placeholder="seu@email.com"
                        value={contactData.email}
                        onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                        required
                        data-testid="contact-email-input"
                        className="h-11 pl-10 border-[#E2E8F0] focus:border-[#0047FF] focus:ring-[#0047FF]"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-contacto" className="text-[#0F172A] font-medium">Contacto Telefónico</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input
                        id="contact-contacto"
                        type="tel"
                        placeholder="912 345 678"
                        value={contactData.contacto}
                        onChange={(e) => setContactData({ ...contactData, contacto: e.target.value })}
                        required
                        data-testid="contact-contacto-input"
                        className="h-11 pl-10 border-[#E2E8F0] focus:border-[#0047FF] focus:ring-[#0047FF]"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-mensagem" className="text-[#0F172A] font-medium">Mensagem</Label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-[#64748B]" />
                      <textarea
                        id="contact-mensagem"
                        placeholder="Escreva a sua mensagem..."
                        value={contactData.mensagem}
                        onChange={(e) => setContactData({ ...contactData, mensagem: e.target.value })}
                        required
                        data-testid="contact-mensagem-input"
                        className="w-full min-h-[100px] pl-10 pt-2 pr-3 pb-2 rounded-md border border-[#E2E8F0] focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF] focus:outline-none resize-none text-sm"
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-[#0047FF] hover:bg-[#0035CC] text-white font-medium"
                    disabled={contactSubmitting}
                    data-testid="contact-submit-btn"
                  >
                    {contactSubmitting ? "A enviar..." : "Enviar Pedido"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
